import { apiConfig, services } from '@project/API';
import { MapMarkerFacade } from './MapMarker';
import { BaseLayersStore } from './BaseLayersStore';
import { CartogramsPageModalStore } from './Cartogram';
import { ControllerStore, MainMenu } from './Controller';
import { CoordsTransform } from './CoordsTransform';
import { EventsStore } from './EventsOpen';
import { GlobalSearch } from './GlobalSearch';
import { HeatmapLayerCreator } from './HeatmapLayerCreator';
import { InitLayersStore } from './InitLayers';
import { InstrumentMenuController } from './InstrumentPanelController';
import { LinkController } from './LinkController';
import { Map as MapLink } from './LinkController/Map';
import { MapStore } from './Map';
import { MapContextMenuController } from './MapContextMenuController';
import { MouseController } from './MouseController';
import { NewsStore } from './News';
import { PhotoMaterial } from './PhotoMaterial';
import { RoadSectionSearch } from './RoadSectionSearch';
import { SearchResultTooltips } from './SearchResultTooltips';
import { SettingsStore } from './Settings';
import { RootNode, TreeViewer } from './shared/LayersTree';
import { RecordCardManager } from './shared/RecordCardManager';
import { LayerTableWindowsController } from './LayerTableCollection/LayerTableWindowsController';
import { UserController } from './User';
import { Search } from './Search';
import { StatisticMenuController } from './StatisticMenuController';
import { EntityLoader } from './EntityLoader';
import { UserLayersStore } from './UserLayers';
import { StatisticViewController } from '@project/store/StatisticViewController';
import { CardController } from './CardController';
import { TableWindowController } from '@project/store/TableWindowView/TableWindowController';
import { FeatureTooltipController } from '@project/store/FeatureTooltipController';
import { MeasureTools } from '@project/store/MeasureTools';

const tableServices = {
  loadTables: services.table.apiTablesList,
  loadTableInfo: services.table.apiTablesRead,
  configuration: apiConfig,
};

export const INIT_MAP_DATA = {
  zoom: 7,
  center: [4421117.719145455, 5978810.284705375],
  srid: 'EPSG:3857',
};

export class RootStore {
  linkController: LinkController;
  map: MapStore;
  user: UserController;
  baseLayers: BaseLayersStore;
  settings: SettingsStore;
  privacyPolicyLinkLoader: EntityLoader<string>;
  mapContextMenu: MapContextMenuController;
  featureTooltipController: FeatureTooltipController;
  instrumentMenu: InstrumentMenuController;
  layerTableWindows: LayerTableWindowsController;
  cartogramsPageModal: CartogramsPageModalStore;
  statisticMenu: StatisticMenuController;
  controller: ControllerStore;
  photoMaterial: PhotoMaterial;
  heatmap: HeatmapLayerCreator;
  searchResultTooltips: SearchResultTooltips;
  SRS: CoordsTransform;
  layerViewer: TreeViewer;
  statisticTableViewer: TreeViewer;
  news: NewsStore;
  events: EventsStore;
  recordCardManager: RecordCardManager;
  mouseController: MouseController;
  initLayers: InitLayersStore;
  globalSearch: GlobalSearch;
  search: Search;
  roadSectionSearch: RoadSectionSearch;
  marker: MapMarkerFacade;
  userLayers: UserLayersStore;
  statisticView: StatisticViewController;
  cardController: CardController;
  tableWindow: TableWindowController;
  measureTools: MeasureTools;

  constructor() {
    this.settings = new SettingsStore(services);
    this.privacyPolicyLinkLoader = new EntityLoader({
      load: services.privacyPolicy.getLink,
    });
    const hashData = MapLink.getInitData();
    const zoom = hashData?.zoom || INIT_MAP_DATA.zoom;
    const center = hashData?.center || INIT_MAP_DATA.center;

    this.map = new MapStore(INIT_MAP_DATA.srid, zoom, center, false, 0);
    this.marker = new MapMarkerFacade(this.map.mapProperties);

    this.mapContextMenu = new MapContextMenuController(
      this.map.mapProperties.map,
    );
    this.featureTooltipController = new FeatureTooltipController(this.map);
    this.instrumentMenu = new InstrumentMenuController();
    this.layerTableWindows = new LayerTableWindowsController(
      this.map,
      this.marker,
    );
    this.tableWindow = new TableWindowController();
    this.cartogramsPageModal = new CartogramsPageModalStore();
    this.statisticMenu = new StatisticMenuController();
    this.statisticView = new StatisticViewController(this.statisticMenu);
    this.linkController = new LinkController(
      this.map.mapProperties,
      this.map.layerCollection,
    );
    this.baseLayers = new BaseLayersStore(
      services,
      this.map.mapProperties,
      this.map.layerCollection,
    );
    this.user = new UserController();
    this.controller = new ControllerStore();
    this.photoMaterial = new PhotoMaterial(
      tableServices,
      this.map.mapProperties,
      this.map.layerCollection,
    );
    this.heatmap = new HeatmapLayerCreator(
      tableServices,
      this.map.layerCollection,
      this.map.mapProperties,
      this.settings,
      this.baseLayers,
    );
    this.searchResultTooltips = new SearchResultTooltips();
    this.SRS = new CoordsTransform(services, this.map.mapProperties);

    this.layerViewer = new TreeViewer(new RootNode(services));
    this.statisticTableViewer = new TreeViewer(new RootNode(services));

    this.news = new NewsStore(services, this.map.mapProperties);
    this.events = new EventsStore(services, this.map.mapProperties);
    this.userLayers = new UserLayersStore(services, this.map.mapProperties);
    this.recordCardManager = new RecordCardManager(services);
    this.mouseController = new MouseController(
      this.map.userActivityManager,
      this.map.mapProperties,
    );
    this.cardController = new CardController(
      this.mouseController,
      this.linkController,
      this.map.layerCollection,
      this.news.newsCard,
      this.events.eventCard,
      this.userLayers.selectedLayer.userLayerRecordCard,
    );
    this.initLayers = new InitLayersStore(
      this.baseLayers,
      this.map.apiMap,
      this.user,
    );
    this.globalSearch = new GlobalSearch(services);
    this.search = new Search(services);
    this.roadSectionSearch = new RoadSectionSearch(
      services,
      this.map.mapProperties,
    );

    this.measureTools = new MeasureTools(this.map.mapProperties.map, {
      showProjMeasurement: false,
    });
  }

  get sideMenuListViewer() {
    if (this.controller.mainMenu === MainMenu.STATISTICS) {
      return this.statisticTableViewer;
    } else {
      return this.layerViewer;
    }
  }
}

/** @deprecated
 *
 * Инициализация стора должна происходить внутри корневого компонента и доступ к стору должен осуществляться только через хук useStore
 *
 * 1. Использование useStore не даст использовать переменную store в классах напрямую => заставит пробрасывать зависимости через конструктор или сеттеры => иерархия классов станет явной
 * 2. Т.к в будущем инициализация RootStore перенесется в компонент, то сайд-эффекты, которые есть в RootStore, не будут влиять на страницы, которым он не нужен (Например MapWidget)
 */
export const store = new RootStore();

declare global {
  interface Window {
    store?: RootStore;
  }
}

if (process.env.DEBUG === 'TRUE') {
  window.store = store;
}
